<template>
  <div class="insight-tags" v-if="allInsightInfo.length">
    <div class="insight-tags__tag-list">
      <BittsTooltip
        v-for="insight in visibleInsightInfo"
        placement="bottom"
        :key="`${insight.label}__${insight.type}`"
      >
        <BittsTag
          :color="
            insight.type === 'partner' ? 'secondary' : 'secondary-outline'
          "
          :text="insight.label"
          size="x-small"
          :slim="true"
        />
        <template #title>
          {{
            `${insight.label} for companies in ${
              insight.type === 'partner' ? 'your' : "Crossbeam's"
            } network`
          }}
        </template>
      </BittsTooltip>
    </div>
    <button
      v-if="collapse && allInsightInfo.length > COLLAPSED_NUMBER_OF_ROLES"
      type="button"
      class="insight-tags__collapse"
      @click.stop="() => (isCollapsed = !isCollapsed)"
    >
      {{
        isCollapsed ? `View all roles (${allInsightInfo.length})` : 'Collapse'
      }}
      <FontAwesomeIcon
        :icon="['fak', isCollapsed ? 'chevron-down' : 'chevron-up']"
        class="text-12 text-neutral-accent"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import { CONTACT_ECOSYSTEM_INSIGHTS } from '@/constants/copilotContacts';
import { Insight, InsightRole } from '@/types/copilotContacts';

const {
  collapse = false,
  partnerInsights = [],
  globalInsights = [],
} = defineProps<{
  collapse?: boolean;
  partnerInsights?: Insight[];
  globalInsights?: Insight[];
}>();

const partnerInsightNames = computed(() =>
  partnerInsights.map((insight) => insight.name),
);
const partnerInsightInfo = computed(() => {
  return CONTACT_ECOSYSTEM_INSIGHTS.filter((insight) =>
    partnerInsightNames.value.includes(insight.value as InsightRole),
  ).map((insight) => {
    return { ...insight, type: 'partner' };
  });
});

const globalInsightNames = computed(() =>
  globalInsights.map((insight) => insight.name),
);
const globalInsightInfo = computed(() => {
  return CONTACT_ECOSYSTEM_INSIGHTS.filter(
    (insight) =>
      globalInsightNames.value.includes(insight.value as InsightRole) &&
      !partnerInsightNames.value.includes(insight.value as InsightRole),
  ).map((insight) => {
    return { ...insight, type: 'global' };
  });
});

const COLLAPSED_NUMBER_OF_ROLES = 2;

const isCollapsed = ref(true);

const allInsightInfo = computed(() => {
  return [...partnerInsightInfo.value, ...globalInsightInfo.value];
});
const visibleInsightInfo = computed(() => {
  if (!collapse || !isCollapsed.value) return allInsightInfo.value;
  return allInsightInfo.value.slice(0, COLLAPSED_NUMBER_OF_ROLES);
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
:deep(.bitts-tag .ant-tag) {
  @apply mr-0 h-full py-0;
}
.bitts-tag {
  @apply flex items-stretch;
}
.insight-tags {
  @apply flex flex-col items-start gap-16;
}
.insight-tags__collapse {
  @apply text-neutral-text-weak text-sm font-bold gap-8;
}
.insight-tags__tag-list {
  @apply flex items-start flex-wrap gap-4;
}
</style>
